import React, { useEffect, useState } from "react";
import { client } from "../../../api/client";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";

interface DashboardStats {
  draft_count: number;
  published_count: number;
  total_count: number;
}

interface Props {
  active: boolean;
  refreshKey?: number;
}

const MigrationDashboard: React.FC<Props> = ({ active, refreshKey = 0 }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    if (!active) return;
    setIsLoading(true);
    try {
      const resp = await client.get<DashboardStats>("/search/admin/migrations/dashboard/");
      setStats(resp.data);
      setError(null);
    } catch (err) {
      setError("Failed to load dashboard statistics.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, refreshKey]);

  if (!stats && isLoading) {
    return (
      <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center h-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
        <p className="text-sm text-red-800">{error}</p>
        <button onClick={fetchStats} className="mt-2 text-xs text-red-700 underline hover:text-red-900">Retry</button>
      </div>
    );
  }

  if (!stats) return null;

  const percentPublished = stats.total_count > 0 
    ? Math.round((stats.published_count / stats.total_count) * 100)
    : 0;

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <h3 className="mb-4 text-base font-semibold text-gray-900">Migration Status</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="p-4 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs font-medium text-gray-500 uppercase">Total LNP Rows</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{stats.total_count.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
          <p className="text-xs font-medium text-blue-600 uppercase">Pending Migration (Drafts)</p>
          <p className="mt-1 text-2xl font-bold text-blue-900">{stats.draft_count.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-lg bg-green-50 border border-green-100">
          <p className="text-xs font-medium text-green-600 uppercase">Migrated to Production</p>
          <p className="mt-1 text-2xl font-bold text-green-900">{stats.published_count.toLocaleString()}</p>
        </div>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1 dark:bg-gray-200">
        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${percentPublished}%` }}></div>
      </div>
      <p className="text-xs text-gray-500 text-right">{percentPublished}% migrated</p>
    </div>
  );
};

export default MigrationDashboard;
